import {
  filter,
  fromEvent,
  groupBy,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  NEVER,
  Observable,
  of,
  scan,
  share,
  Subject,
  switchMap,
  take,
} from 'rxjs'
import { v4 } from 'uuid'

import { isNotNull } from '../../utils/collectionTools'
import {
  getPageState as defaultGetPageState,
  pageStateChanges$ as defaultPageStateChanges$,
} from '../../utils/pageState'
import { localOrFallbackStorage } from '../../utils/storage'

const CHANNEL_NAME = 'dfh-cross-tab'

export type CrossTabMessage =
  | { readonly _tag: 'auth'; readonly subject: string | null; readonly expiresAt: number | null }
  | {
      readonly _tag: 'cart'
      readonly cartId: string
      readonly kind: 'guest' | 'customer'
      readonly fingerprint: string
    }
  | { readonly _tag: 'dispensary' }
  | { readonly _tag: 'escript'; readonly status: 'idle' | 'in_progress' }

export interface CrossTabEnvelope {
  readonly source: string
  readonly seq: number
  readonly sentAt: number
  readonly message: CrossTabMessage
}

export interface CrossTabTransport {
  readonly post: (envelope: CrossTabEnvelope) => void
  readonly messages$: Observable<CrossTabEnvelope>
}

export interface CrossTabChannel {
  readonly publish: (message: CrossTabMessage) => void
  readonly incoming$: Observable<CrossTabEnvelope>
}

type PageState = ReturnType<typeof defaultGetPageState>

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// Ids and hashes on the channel are short (a UUID, a masked cart id, an FNV-1a hex), so a cap
// bounds the size of each entry in the per-source state every tab holds. The number of sources
// is not bounded.
const MAX_STRING_LENGTH = 128

const isShortString = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= MAX_STRING_LENGTH

const isShortStringOrNull = (value: unknown): value is string | null =>
  value === null || isShortString(value)

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isCrossTabMessage = (value: unknown): value is CrossTabMessage => {
  if (!isRecord(value)) {
    return false
  }
  switch (value._tag) {
    case 'auth':
      return (
        isShortStringOrNull(value.subject) &&
        (value.expiresAt === null || isFiniteNumber(value.expiresAt))
      )
    case 'cart':
      return (
        isShortString(value.cartId) &&
        (value.kind === 'guest' || value.kind === 'customer') &&
        isShortString(value.fingerprint)
      )
    case 'dispensary':
      return true
    case 'escript':
      return value.status === 'idle' || value.status === 'in_progress'
    default:
      return false
  }
}

export const isCrossTabEnvelope = (value: unknown): value is CrossTabEnvelope =>
  isRecord(value) &&
  isShortString(value.source) &&
  typeof value.seq === 'number' &&
  Number.isSafeInteger(value.seq) &&
  value.seq > 0 &&
  isFiniteNumber(value.sentAt) &&
  isCrossTabMessage(value.message)

const parseJson = (text: string): unknown => {
  try {
    const parsed: unknown = JSON.parse(text)
    return parsed
  } catch {
    return undefined
  }
}

export const createBroadcastChannelTransport = (name: string): CrossTabTransport => {
  const channel = new BroadcastChannel(name)
  return {
    post: (envelope) => channel.postMessage(envelope),
    messages$: fromEvent<MessageEvent<unknown>>(channel, 'message').pipe(
      map((event) => event.data),
      filter(isCrossTabEnvelope),
      share(),
    ),
  }
}

/**
 * Fallback for browsers without `BroadcastChannel`. The key is removed in the same tick it is set,
 * so nothing persists; the removal fires a second event with a null `newValue`, which receivers
 * ignore.
 */
export const createStorageTransport = (key: string): CrossTabTransport => ({
  post: (envelope) => {
    try {
      localOrFallbackStorage.setItem(key, JSON.stringify(envelope))
      localOrFallbackStorage.removeItem(key)
    } catch {
      // Best effort: a full or blocked storage only costs other tabs this announcement.
    }
  },
  messages$: fromEvent<StorageEvent>(window, 'storage').pipe(
    map((event) => (event.key === key ? event.newValue : null)),
    filter(isNotNull),
    map(parseJson),
    filter(isCrossTabEnvelope),
    share(),
  ),
})

const noopTransport: CrossTabTransport = { post: () => undefined, messages$: NEVER }

export const createDefaultTransport = (): CrossTabTransport =>
  typeof window === 'undefined'
    ? noopTransport
    : typeof BroadcastChannel !== 'undefined'
      ? createBroadcastChannelTransport(CHANNEL_NAME)
      : createStorageTransport(CHANNEL_NAME)

const createTabId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : v4()

interface DedupeState {
  readonly highestSeqBySource: ReadonlyMap<string, number>
  readonly accepted: CrossTabEnvelope | null
}

// The per-source `seq` check guards against the storage fallback replaying a value. Neither
// transport echoes to its sender, so the own-source filter is only a backstop.
export const dedupeBySource =
  (ownTabId: string): MonoTypeOperatorFunction<CrossTabEnvelope> =>
  (source$) =>
    source$.pipe(
      filter((envelope) => envelope.source !== ownTabId),
      scan<CrossTabEnvelope, DedupeState>(
        ({ highestSeqBySource }, envelope) => {
          const highest = highestSeqBySource.get(envelope.source)
          return highest !== undefined && envelope.seq <= highest
            ? { highestSeqBySource, accepted: null }
            : {
                highestSeqBySource: new Map(highestSeqBySource).set(envelope.source, envelope.seq),
                accepted: envelope,
              }
        },
        { highestSeqBySource: new Map(), accepted: null },
      ),
      map(({ accepted }) => accepted),
      filter(isNotNull),
    )

const isVisible = (pageState: PageState): boolean =>
  pageState === 'active' || pageState === 'passive'

export interface VisibilityGate {
  readonly getPageState?: () => PageState
  readonly pageStateChanges$?: Observable<PageState>
  readonly groupKey?: (envelope: CrossTabEnvelope) => string
}

const byTag = (envelope: CrossTabEnvelope): string => envelope.message._tag

/** A gate key that holds one message per sending tab, where the latest per tag would drop a peer's. */
export const byTagAndSource = ({ source, message }: CrossTabEnvelope): string =>
  `${message._tag}:${source}`

/**
 * Holds each envelope until the page is visible, keeping only the latest per `groupKey` (the
 * `_tag` by default) while it waits, so a hidden tab does not race the visible one (the
 * `useStorage` rule).
 */
export const latestVisibleByTag =
  ({
    getPageState = defaultGetPageState,
    pageStateChanges$ = defaultPageStateChanges$,
    groupKey = byTag,
  }: VisibilityGate = {}): MonoTypeOperatorFunction<CrossTabEnvelope> =>
  (source$) =>
    source$.pipe(
      groupBy(groupKey),
      mergeMap((byTag$) =>
        byTag$.pipe(
          switchMap((envelope) =>
            isVisible(getPageState())
              ? of(envelope)
              : pageStateChanges$.pipe(
                  filter(isVisible),
                  take(1),
                  map(() => envelope),
                ),
          ),
        ),
      ),
    )

export const createCrossTabChannel = ({
  transport,
  tabId,
}: {
  readonly transport: CrossTabTransport
  readonly tabId: string
}): CrossTabChannel => {
  const outgoing$ = new Subject<CrossTabMessage>()
  // Held for the life of the channel, so `publish` posts synchronously from the first call.
  outgoing$
    .pipe(
      scan<CrossTabMessage, CrossTabEnvelope, null>(
        (previous, message) => ({
          source: tabId,
          seq: (previous?.seq ?? 0) + 1,
          sentAt: Date.now(),
          message,
        }),
        null,
      ),
    )
    .subscribe(transport.post)
  return {
    publish: (message) => outgoing$.next(message),
    incoming$: transport.messages$.pipe(dedupeBySource(tabId), share()),
  }
}

export const crossTabChannel: CrossTabChannel = createCrossTabChannel({
  transport: createDefaultTransport(),
  tabId: createTabId(),
})
